/**
 * The marketing site's analytics.
 *
 * The key is a write-only ingest key and is public by design -- it is served
 * to every visitor inside the bundle. CRA inlines REACT_APP_* at build time,
 * so the override exists for a preview deploy, not for secrecy.
 */
const PRODUCTION_KEY = "phc_A3fv3NK8SERLSCswPV3b6DJLR3McR8FTXkFsrwsnUGdA";
const PRODUCTION_HOST = "https://eu.i.posthog.com";

const key = process.env.REACT_APP_POSTHOG_KEY?.trim() || PRODUCTION_KEY;
const host = process.env.REACT_APP_POSTHOG_HOST?.trim() || PRODUCTION_HOST;

/**
 * Pure, so the three decisions that matter are testable without the SDK.
 *
 * Autocapture is ON here and OFF in the console, deliberately. This is a
 * brochure site with no personal data on screen and little traffic, so
 * recording every click is cheap and is what lets a question be asked months
 * later about a button that was never instrumented. The console renders
 * member names and amounts, so the same setting there would leak them into
 * element text.
 */
export const analyticsOptions = () => ({
  api_host: host,
  person_profiles: "identified_only",
  autocapture: true,
  enable_heatmaps: true,
  capture_pageview: true,
  // Rejected in the design, not merely left at its default: see
  // docs/superpowers/specs/2026-09-22-posthog-analytics-design.md §4.
  disable_session_recording: true,
  // The surveys script is a separate 34 KB download we never use.
  disable_surveys: true,
});

/*
 * The SDK is the largest thing in the bundle (about 250 KB before zipping), so
 * it is fetched after the page is up rather than with it. Until it arrives the
 * client is null, and track.js drops events rather than queue them; the
 * pageview is captured by the SDK itself once it initialises.
 */
let client = null;

const analytics = {
  get __loaded() {
    return Boolean(client?.__loaded);
  },
  capture: (...args) => client?.capture(...args),
};

const whenIdle = (fn) => {
  const run = () =>
    "requestIdleCallback" in window
      ? window.requestIdleCallback(fn, { timeout: 4000 })
      : window.setTimeout(fn, 1500);
  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });
};

export const initAnalytics = () => {
  if (!key || typeof window === "undefined") return;
  // The prerender's headless browser is not a visitor.
  if (navigator.webdriver) return;
  whenIdle(() => {
    import("posthog-js")
      .then(({ default: posthog }) => {
        posthog.init(key, analyticsOptions());
        client = posthog;
      })
      .catch(() => {
        /* analytics is best effort */
      });
  });
};

export default analytics;
